import { isMobileOrTablet } from '../../../common/core-layer/utils/core.utils'

export const SUPPORT_CHAT_WIDGET_MOUNT_ID = 'clientportal-chatWidget'

/** Stacking order for the third-party launcher — above CP topbar (40) and course overlays. */
export const SUPPORT_CHAT_WIDGET_Z_INDEX = '10000'

/** Set on the mount host after a successful inject so we do not treat another location/widget’s `<chat-widget>` as ours. */
export const SUPPORT_CHAT_WIDGET_EMBED_KEY_ATTR = 'data-cp-support-chat-embed-key'

/** Mobile offsets for LeadConnector launcher and teaser bubble above mobile bottom nav / lesson actions. */
export const SUPPORT_CHAT_MOBILE_BUBBLE_BOTTOM = '60px'
export const SUPPORT_CHAT_MOBILE_PROMPT_BOTTOM = '40px'

export function getSupportChatWidgetMountElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  const mount = document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)
  return mount instanceof HTMLElement ? mount : null
}

/** Prefer widget under our mount; fall back to document only when this session owns an embed. */
export function findSupportChatWidgetElement(): Element | null {
  const mount = getSupportChatWidgetMountElement()
  const inMount = mount?.querySelector('chat-widget')
  if (inMount) return inMount
  if (getSupportChatMountedEmbedKey()) {
    return document.querySelector('chat-widget')
  }
  return null
}

export function serializeSupportChatEmbedMountKey(locationId: string, widgetId: string): string {
  return JSON.stringify({ locationId, widgetId })
}

export function getSupportChatMountedEmbedKey(): string | null {
  if (typeof document === 'undefined') return null
  const mount = document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)
  return mount?.getAttribute(SUPPORT_CHAT_WIDGET_EMBED_KEY_ATTR) ?? null
}

export function setSupportChatMountedEmbedKey(key: string): void {
  if (typeof document === 'undefined') return
  document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)?.setAttribute(SUPPORT_CHAT_WIDGET_EMBED_KEY_ATTR, key)
}

function clearSupportChatMountedEmbedKey(): void {
  if (typeof document === 'undefined') return
  document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)?.removeAttribute(SUPPORT_CHAT_WIDGET_EMBED_KEY_ATTR)
}

/** True when LiveChat (or similar) has already booted for this portal session — used to skip refetch/re-inject on route changes. */
export function isSupportChatWidgetInDocument(): boolean {
  if (typeof document === 'undefined') return false
  return Boolean(findSupportChatWidgetElement())
}

declare global {
  interface Window {
    /** MutationObserver from `attachSupportChatWidgetDomHooks` while waiting for `<chat-widget>`. */
    __cpSupportChatWidgetMountObserver?: MutationObserver
    __cpSupportChatWidgetMountObserverTimeoutId?: ReturnType<typeof setTimeout>
    /** MutationObserver for mobile bubble/prompt positioning. */
    __cpSupportChatMobilePositionObserver?: MutationObserver
    /** Re-exported observer reference for compatibility with clientportal-core / hmi. */
    __lcWidgetStyleObserver?: MutationObserver
    /** Event listener reference for LC_chatWidgetLoaded cleanup. */
    __cpSupportChatMobilePositionLoadedHandler?: (event: Event) => void
  }
}

function clearSupportChatWidgetDomHookObserver(): void {
  const w = globalThis as unknown as Window
  const timeoutId = w.__cpSupportChatWidgetMountObserverTimeoutId
  if (timeoutId !== undefined) {
    clearTimeout(timeoutId)
    w.__cpSupportChatWidgetMountObserverTimeoutId = undefined
  }
  const mountObserver = w.__cpSupportChatWidgetMountObserver
  if (mountObserver) {
    mountObserver.disconnect()
    w.__cpSupportChatWidgetMountObserver = undefined
  }
}

export function clearSupportChatWidgetMobilePositioning(): void {
  const w = globalThis as unknown as Window
  if (w.__cpSupportChatMobilePositionObserver) {
    w.__cpSupportChatMobilePositionObserver.disconnect()
    w.__cpSupportChatMobilePositionObserver = undefined
  }
  if (w.__lcWidgetStyleObserver) {
    w.__lcWidgetStyleObserver.disconnect()
    w.__lcWidgetStyleObserver = undefined
  }
  if (w.__cpSupportChatMobilePositionLoadedHandler && typeof window !== 'undefined') {
    window.removeEventListener('LC_chatWidgetLoaded', w.__cpSupportChatMobilePositionLoadedHandler)
    w.__cpSupportChatMobilePositionLoadedHandler = undefined
  }
}

export function clearSupportChatWidgetMount(): void {
  if (typeof document === 'undefined') return

  clearSupportChatWidgetDomHookObserver()
  clearSupportChatWidgetMobilePositioning()

  const widget = findSupportChatWidgetElement()
  const mount = document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)
  if (mount) {
    clearSupportChatMountedEmbedKey()
    mount.innerHTML = ''
  }

  widget?.remove()
}

function applyMountHostStyles(mount: HTMLElement): void {
  mount.style.setProperty('position', 'fixed')
  mount.style.setProperty('inset', '0')
  mount.style.setProperty('z-index', SUPPORT_CHAT_WIDGET_Z_INDEX)
  mount.style.setProperty('pointer-events', 'none')
}

function applyChatHostStyles(element: Element): void {
  const el = element as HTMLElement
  el.style.setProperty('z-index', SUPPORT_CHAT_WIDGET_Z_INDEX)
  el.style.setProperty('pointer-events', 'auto')
}

export function applySupportChatMobileStyles(container: Element): void {
  if (container instanceof HTMLElement) {
    if (container.classList.contains('lc_text-widget--bubble')) {
      container.style.setProperty('bottom', SUPPORT_CHAT_MOBILE_BUBBLE_BOTTOM, 'important')
    }
    if (container.classList.contains('lc_text-widget--prompt')) {
      container.style.setProperty('bottom', SUPPORT_CHAT_MOBILE_PROMPT_BOTTOM, 'important')
    }
  }

  const btn = container.querySelector?.('.lc_text-widget--bubble') as HTMLElement | null
  if (btn?.style?.setProperty) {
    btn.style.setProperty('bottom', SUPPORT_CHAT_MOBILE_BUBBLE_BOTTOM, 'important')
  }

  const prompt = container.querySelector?.('.lc_text-widget--prompt') as HTMLElement | null
  if (prompt?.style?.setProperty) {
    prompt.style.setProperty('bottom', SUPPORT_CHAT_MOBILE_PROMPT_BOTTOM, 'important')
  }
}

export function applySupportChatWidgetMobilePositioning(container?: Element | null): void {
  if (typeof document === 'undefined') return

  const target = container ?? findSupportChatWidgetElement() ?? getSupportChatWidgetMountElement()
  if (!target) return

  applySupportChatMobileStyles(target)

  const w = globalThis as unknown as Window
  if (w.__cpSupportChatMobilePositionObserver) {
    w.__cpSupportChatMobilePositionObserver.disconnect()
    w.__cpSupportChatMobilePositionObserver = undefined
  }

  const observer = new MutationObserver(mutationsList => {
    for (const mutation of mutationsList) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element
          if (
            el.classList?.contains('lc_text-widget--bubble') ||
            el.classList?.contains('lc_text-widget--prompt') ||
            el.querySelector?.('.lc_text-widget--bubble') ||
            el.querySelector?.('.lc_text-widget--prompt')
          ) {
            applySupportChatMobileStyles(target)
            return
          }
        }
      }
    }
  })

  observer.observe(target, {
    childList: true,
    subtree: true,
  })

  w.__cpSupportChatMobilePositionObserver = observer
  w.__lcWidgetStyleObserver = observer
}

/**
 * After postscribe completes, find <chat-widget> (immediate or via MutationObserver) and style it.
 */
export function attachSupportChatWidgetDomHooks(): void {
  if (typeof document === 'undefined') return

  clearSupportChatWidgetDomHookObserver()
  clearSupportChatWidgetMobilePositioning()

  const mount = getSupportChatWidgetMountElement()
  if (mount) {
    applyMountHostStyles(mount)
  }

  const applyHostStyles = (widget: Element) => {
    applyChatHostStyles(widget)
    if (isMobileOrTablet()) {
      applySupportChatWidgetMobilePositioning(widget)
    }
  }

  if (isMobileOrTablet() && typeof window !== 'undefined') {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<Element>
      const container = customEvent.detail ?? findSupportChatWidgetElement()
      if (container) {
        applySupportChatWidgetMobilePositioning(container)
      }
    }
    const w = globalThis as unknown as Window
    w.__cpSupportChatMobilePositionLoadedHandler = handler
    window.addEventListener('LC_chatWidgetLoaded', handler, { once: true })
  }

  const existing = findSupportChatWidgetElement()
  if (existing) {
    applyHostStyles(existing)
    return
  }

  const w = globalThis as unknown as Window
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element
          const widget = element.tagName?.toLowerCase() === 'chat-widget' ? element : element.querySelector('chat-widget')

          if (widget) {
            applyHostStyles(widget)
            clearSupportChatWidgetDomHookObserver()
            return
          }
        }
      }
    }
  })

  w.__cpSupportChatWidgetMountObserver = observer
  const observeTarget = mount ?? document.body
  observer.observe(observeTarget, { childList: true, subtree: true })
  w.__cpSupportChatWidgetMountObserverTimeoutId = globalThis.setTimeout(() => {
    clearSupportChatWidgetDomHookObserver()
  }, 10000)
}
